import * as React from "react";
import { MouseEvent, useState } from "react";
import { useSelector, useStore } from "react-redux";

import { Button, FormControlLabel, Menu, MenuItem, Radio, RadioGroup } from "@mui/material";

import { InputField } from "./InputField.tsx";
import { useConfig } from "../hooks/useConfig.ts";

import {
  setSpendDetailsRows,
} from "../Store/roiStore.ts";
import {
  CUSTOMER_SITUATION,
  EXISTING_SCALE_COUNT_KEY,
  FACILITY_STATUS,
  FACILITY_STATUS_KEY,
  NEW_SCALE_COUNT_KEY,
  SCALE_COUNT_KEY,
  defaultFacilityStatus,
  isExistingCustomerMode,
  resolveCustomerSituation,
  resolveFacilityScales,
} from "../utils/customerSituation.ts";

export const Row = ({ onChange, onAddRow, onRemoveRow, rowId }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const config = useConfig();
  const spendRowsConfig = config.inputFields.spendDetailsRow;
  const spendDetailRows = useSelector(
    (state) => state.inputFields.spendDetailsRows
  );
  const isSameDayIncluded = useSelector(
    (state) =>
      !state.inputFields.sameDay?.isSameDayOnly &&
      (state.inputFields.client?.currentParSolutions ?? []).includes("PAR SameDay")
  );
  const situation = useSelector((state) =>
    resolveCustomerSituation(state.inputFields.client)
  );
  const existingMode = isExistingCustomerMode(situation);
  const rows = spendRowsConfig.filter((field) => {
    if (!isSameDayIncluded && field.label === "Annual Bill Only Spend") return false;
    if (existingMode && field.label === SCALE_COUNT_KEY) return false;
    return true;
  });
  const { dispatch } = useStore();
  const rowKey = "row" + rowId;
  const facility = spendDetailRows?.[rowKey] ?? {};
  const scaleSplit = resolveFacilityScales(facility, situation);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const addRow = () => {
    if (!onAddRow) return;
    onAddRow(rowId);
    setAnchorEl(null);
  };

  const removeRow = () => {
    if (!onAddRow) return;
    onRemoveRow(rowId);
    setAnchorEl(null);
  };

  const setField = (name, value) => {
    dispatch(
      setSpendDetailsRows({
        rowId: rowId,
        name: name,
        value: value,
      })
    );
  };

  const syncScaleCount = (existingScales, newScales) => {
    setField(EXISTING_SCALE_COUNT_KEY, existingScales);
    setField(NEW_SCALE_COUNT_KEY, newScales);
    setField(SCALE_COUNT_KEY, Number(existingScales) + Number(newScales));
  };

  const handleStatusChange = (nextStatus) => {
    setField(FACILITY_STATUS_KEY, nextStatus);
    if (nextStatus === FACILITY_STATUS.UNSCALED) {
      const moved = scaleSplit.totalScales || scaleSplit.newScales;
      syncScaleCount(0, moved);
    } else if (situation === CUSTOMER_SITUATION.PAR360_ADDON) {
      const moved = scaleSplit.totalScales || scaleSplit.existingScales;
      syncScaleCount(moved, 0);
    } else {
      syncScaleCount(scaleSplit.existingScales || scaleSplit.totalScales, scaleSplit.newScales);
    }
  };

  const getHelperText = (index, row, value, label) => {
    if (spendDetailRows?.[row]?.[label] === undefined) return;

    switch (label) {
      case "Inventory Count Frequency per Week":
        return parseFloat(value) > 7
          ? "Must be 7 or less (days per week, per location)"
          : false;

      default:
        return String(value ?? "").trim().length === 0 && String(value ?? "") !== "0"
          ? "Can't be zero or empty"
          : false;
    }
  };

  const getSoftNote = (fieldKey, value) => {
    if (fieldKey !== SCALE_COUNT_KEY && fieldKey !== EXISTING_SCALE_COUNT_KEY && fieldKey !== NEW_SCALE_COUNT_KEY) {
      return "";
    }
    const skus = parseFloat(spendDetailRows?.[rowKey]?.["Total SKUs"]);
    const scales = fieldKey === SCALE_COUNT_KEY
      ? parseFloat(value)
      : scaleSplit.totalScales;
    return Number.isFinite(skus) && Number.isFinite(scales) && scales > skus
      ? "Scales are usually fewer than SKUs — double-check this number."
      : "";
  };

  const renderStatusAndScales = () => {
    if (!existingMode) return null;
    const status = facility[FACILITY_STATUS_KEY] || defaultFacilityStatus(situation);
    const hideNewScales = situation === CUSTOMER_SITUATION.PAR360_ADDON;

    return (
      <>
        <div className="field-wrapper field-wrapper-wide">
          <div className="field-label">Facility status</div>
          <RadioGroup
            row
            value={status}
            onChange={(event) => handleStatusChange(event.target.value)}
          >
            <FormControlLabel
              value={FACILITY_STATUS.ALREADY_SCALED}
              control={<Radio size="small" />}
              label="Already scaled"
            />
            <FormControlLabel
              value={FACILITY_STATUS.UNSCALED}
              control={<Radio size="small" />}
              label="Unscaled / new"
            />
          </RadioGroup>
        </div>
        {status === FACILITY_STATUS.ALREADY_SCALED && (
          <div className="field-wrapper">
            <div className="field-label">Existing PAR Scales (installed)</div>
            <InputField
              helperText="Installed base — already delivering scale savings."
              value={scaleSplit.existingScales}
              hiddenLabel
              type="number"
              defaultValue="0"
              onValueInputChange={(_name, value) =>
                syncScaleCount(value, hideNewScales ? 0 : scaleSplit.newScales)
              }
            />
          </div>
        )}
        {!hideNewScales && (
          <div className="field-wrapper">
            <div className="field-label">
              {status === FACILITY_STATUS.UNSCALED
                ? "New PAR Scales to deploy"
                : "New scales in this deal"}
            </div>
            <InputField
              helperText={
                status === FACILITY_STATUS.UNSCALED
                  ? "All scales at this new site."
                  : "Only these create new scale savings, subscription, or CAPEX."
              }
              value={scaleSplit.newScales}
              hiddenLabel
              type="number"
              defaultValue="0"
              onValueInputChange={(_name, value) =>
                syncScaleCount(
                  status === FACILITY_STATUS.UNSCALED ? 0 : scaleSplit.existingScales,
                  value
                )
              }
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="row">
      <div className="rowId">Facility {rowId}</div>
      <div className="subrow">
        {rows.map((field, index) => {
          const label = field.displayLabel ?? field.label;
          const fieldKey = field.label;
          const fieldValue =
            spendDetailRows?.[rowKey]?.[fieldKey] ??
            field.defaultValue ??
            (field.type === "number" ? 0 : "");
          const validationMessage = getHelperText(
            index,
            rowKey,
            fieldValue,
            field.label
          );
          const softNote = getSoftNote(fieldKey, fieldValue);

          return (
            <React.Fragment key={fieldKey}>
              <div className="field-wrapper">
                <div className="field-label">{label}</div>
                <InputField
                  error={validationMessage || softNote}
                  helperText={validationMessage || softNote || field.smallNote || ""}
                  value={fieldValue}
                  placeholder={field.placeholder}
                  hiddenLabel
                  onValueInputChange={(name, value) =>
                    dispatch(
                      setSpendDetailsRows({
                        rowId: rowId,
                        index: index,
                        name: fieldKey,
                        value: value,
                      })
                    )
                  }
                  {...(field.prefix && { prefix: field.prefix })}
                  {...(field.suffix && { suffix: field.suffix })}
                  {...(field.type && { type: field.type })}
                  {...(field.defaultValue && {
                    defaultValue: field.defaultValue,
                  })}
                />
              </div>
              {field.label === "Facility Name" && renderStatusAndScales()}
            </React.Fragment>
          );
        })}
      </div>
      <div className="rowActions">
        <Button
          id="basic-button"
          aria-controls={open ? "basic-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
        >
          Actions
        </Button>
        <Menu
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={addRow}>Add Facility</MenuItem>
          <MenuItem onClick={removeRow}>Delete Facility</MenuItem>
        </Menu>
      </div>
    </div>
  );
};
